import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, ButtonGroup } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    title: '',
    contactMethod: 'email', // default contact method
    email: '',
    socialUsername: '',
    message: '',
  });
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState(null);
  const [userIp, setUserIp] = useState(window.ip || "none");
  const [userId, setUserId] = useState(window.userHash || 'none');
  const location = useLocation();

  const contactMethods = [
    { value: 'email', label: 'Email' },
    { value: 'twitter', label: 'Twitter (X)' },
    { value: 'discord', label: 'Discord' },
    { value: 'telegram', label: 'Telegram' },
    { value: 'session', label: 'Session' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'other', label: 'Other Social Media' }
  ];

  // Function to get URL parameters
  const getParameterByName = (name) => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name) || '';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    if (selectedFiles.length > 10) {
      setAlert({
        type: 'danger',
        message: 'Maximum 10 files allowed'
      });
      return;
    }

    const oversizedFiles = selectedFiles.filter(file => file.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setAlert({
        type: 'danger',
        message: 'Some files exceed the 10MB limit'
      });
      return;
    }

    setFiles(selectedFiles);
    setAlert(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setAlert(null);

    try {
      const payload = new FormData();

      // Get contact info based on selected method
      const contactInfo = formData.contactMethod === 'email' 
        ? formData.email 
        : formData.socialUsername;

      // Create embed object
      const embed = {
        title: formData.title,
        description: formData.message,
        fields: [
          { name: 'Contact Method', value: formData.contactMethod, inline: true },
          { name: 'Contact Info', value: contactInfo, inline: true },
          { name: 'User-Agent', value: navigator.userAgent, inline: true },
          { name: 'User ID', value: userId, inline: true },
          { name: 'IP', value: userIp, inline: true },
          { name: 'From URL', value: getParameterByName("from") || document.referrer || document.referer || "none", inline: true },
          { name: 'Invited by', value: getParameterByName("ref") || 'None', inline: true }
        ],
        color: 0x7289DA
      };

      // Add the embed to the payload as JSON
      payload.append('payload_json', JSON.stringify({ embeds: [embed] }));

      // Add files to payload
      files.forEach((file, i) => {
        payload.append(`file[${i}]`, file);
      });

      // Replace YOUR_WEBHOOK_URL with actual Discord webhook URL
      const response = await fetch(window.contactUsWebhook, {
        method: 'POST',
        body: payload
      });

      if (response.ok) {
        setAlert({
          type: 'success',
          message: 'Message sent successfully!'
        });
        // Reset form
        setFormData({ title: '', contactMethod: 'email', email: '', socialUsername: '', message: '' });
        setFiles([]);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      setAlert({
        type: 'danger',
        message: 'Failed to send message. Please try again.'
      });
      console.error('Error sending message:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="row justify-content-center">
      <div className="col-12 col-md-8">
        <ButtonGroup>
          <Button variant="outline-primary" onClick={() => navigate('/')}>
            Home
          </Button>
        </ButtonGroup>
        <Card data-bs-theme="dark" className="bg-dark border-light">
          <Card.Body>
            <Card.Title>Contact Us</Card.Title>
            {alert && (
              <Alert variant={alert.type} className="mt-3">
                {alert.message}
              </Alert>
            )}
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  className="bg-dark text-light"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Preferred Contact Method</Form.Label>
                <Form.Select
                  name="contactMethod"
                  value={formData.contactMethod}
                  onChange={handleInputChange}
                  className="bg-dark text-light"
                >
                  {contactMethods.map(method => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {formData.contactMethod === 'email' ? (
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="bg-dark text-light"
                  />
                </Form.Group>
              ) : (
                <Form.Group className="mb-3">
                  <Form.Label>{`${contactMethods.find(m => m.value === formData.contactMethod)?.label} Username/Handle`}</Form.Label>
                  <Form.Control
                    type="text"
                    name="socialUsername"
                    value={formData.socialUsername}
                    onChange={handleInputChange}
                    required
                    placeholder={`Enter your ${formData.contactMethod} username or handle`}
                    className="bg-dark text-light"
                  />
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Message</Form.Label>
                <Form.Control
                  as="textarea"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  className="bg-dark text-light"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Attachments (Optional, max 10 files, 10MB each)</Form.Label>
                <Form.Control
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="bg-dark text-light"
                  accept="*/*"
                />
                <Form.Text className="text-muted">
                  Selected files: {files.length}
                </Form.Text>
              </Form.Group>

              <Button
                variant="primary"
                type="submit"
                disabled={isSubmitting}
                className="w-100"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </Form>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default ContactUs;